<div class="space-y-6">
  <app-page-header
    [title]="'profile.monProfil' | t"
    [subtitle]="'profile.tesInformationsPersonnellesTon' | t"
  />

  @if (user(); as u) {
    <!-- Header card : avatar + name + email + roles -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-4">
          @if (u.avatarUrl) {
            <img [src]="u.avatarUrl" [alt]="displayName()" class="h-20 w-20 rounded-full object-cover ring-2 ring-brand-100 dark:ring-brand-900/40" />
          } @else {
            <span class="flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 text-xl font-semibold text-brand-700 ring-2 ring-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:ring-brand-900/40">
              {{ userInitials() }}
            </span>
          }
          <div class="min-w-0 space-y-1">
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white/90">{{ displayName() }}</h2>
            @if (u.email) {
              <p class="truncate text-sm text-gray-500 dark:text-gray-400">{{ u.email }}</p>
            }
            @if (businessRoles().length > 0) {
              <div class="flex flex-wrap gap-1.5 pt-1">
                @for (r of businessRoles(); track r) {
                  <span class="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ r }}</span>
                }
              </div>
            }
          </div>
        </div>
        @if (u.active === false) {
          <span class="inline-flex h-fit items-center rounded-full bg-error-50 px-3 py-1 text-xs font-medium text-error-700 dark:bg-error-500/10 dark:text-error-400">
            {{ 'profile.compteDesactive' | t }}
          </span>
        }
      </div>
    </section>

    <!-- Profil propriétaire (business profile via /api/vehicle-owners/me) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'profile.profilProprietaire' | t }}</h3>
          @if (ownerProfile(); as o) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              @if (o.ownerType === 'COMPANY') {
                <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ 'profile.societe' | t }}</span>
              } @else {
                <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">Particulier</span>
              }
              @if (o.instantBooking) {
                <span class="ml-1.5 inline-flex items-center rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-medium text-success-700 dark:bg-success-500/15 dark:text-success-400">
                  {{ 'profile.reservationImmediate' | t }}
                </span>
              }
            </p>
          }
        </div>
        @if (ownerProfile()) {
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              (click)="openType()"
              class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              {{ 'profile.typeDeCompte' | t }}
            </button>
            <button
              type="button"
              (click)="openBiz()"
              class="inline-flex items-center gap-1.5 rounded-lg border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 transition hover:bg-brand-50 dark:border-brand-800 dark:text-brand-400 dark:hover:bg-brand-500/10"
            >
              {{ 'profile.modifier' | t }}
            </button>
          </div>
        }
      </div>

      @if (ownerLoading()) {
        <p class="mt-4 text-sm text-gray-400">{{ 'profile.chargementDuProfilProprietaire' | t }}</p>
      } @else if (ownerError() && !ownerProfile()) {
        <div class="mt-4 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-500/10 dark:text-warning-300">
          {{ ownerError() }}
        </div>
      } @else if (ownerProfile(); as o) {
        <div class="mt-5 grid gap-6 sm:grid-cols-2">
          <!-- Bloc gauche : infos -->
          <dl class="grid gap-3 text-sm">
            @if (isCompany()) {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Raison sociale</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">{{ o.businessName || '—' }}</dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">Email pro</dt>
                <dd class="break-all text-right text-gray-800 dark:text-white/90">{{ o.businessEmail || '—' }}</dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.telephonePro' | t }}</dt>
                <dd class="text-right font-mono text-gray-800 dark:text-white/90">{{ o.businessPhone || '—' }}</dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.adressePro' | t }}</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">{{ o.businessAddress || '—' }}</dd>
              </div>
            } @else {
              <div class="flex justify-between gap-4">
                <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.type' | t }}</dt>
                <dd class="text-right text-gray-800 dark:text-white/90">Particulier</dd>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Bascule en <strong>{{ 'profile.societe' | t }}</strong> {{ 'profile.pourSaisirTesInformations' | t }}
              </p>
            }
            <div class="flex justify-between gap-4 border-t border-gray-100 pt-3 dark:border-gray-800">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.tempsDeReponseMoyen' | t }}</dt>
              <dd class="text-right text-gray-800 dark:text-white/90">{{ o.responseTime || '—' }}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.reservationImmediate' | t }}</dt>
              <dd class="text-right text-gray-800 dark:text-white/90">{{ o.instantBooking ? 'Oui' : 'Non' }}</dd>
            </div>
          </dl>

          <!-- Bloc droite : statistiques -->
          <div class="grid grid-cols-2 gap-3 sm:gap-4">
            <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'profile.vehicules' | t }}</p>
              <p class="mt-1 text-xl font-semibold text-gray-800 dark:text-white/90">{{ o.totalVehicles ?? 0 }}</p>
            </div>
            <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Locations</p>
              <p class="mt-1 text-xl font-semibold text-gray-800 dark:text-white/90">{{ o.totalRentals ?? 0 }}</p>
            </div>
            <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Note moyenne</p>
              <p class="mt-1 text-xl font-semibold text-gray-800 dark:text-white/90">
                @if (o.averageRating != null) { {{ o.averageRating }} <span class="text-xs font-normal text-gray-400">/ 5</span> } @else { — }
              </p>
            </div>
            <div class="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <p class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Revenus totaux</p>
              <p class="mt-1 text-xl font-semibold text-gray-800 dark:text-white/90">
                @if (o.totalEarnings != null) { {{ o.totalEarnings }} <span class="text-xs font-normal text-gray-400">XOF</span> } @else { — }
              </p>
            </div>
          </div>
        </div>
      }
    </section>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- Personal info -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Informations personnelles</h3>
          <button
            type="button"
            (click)="openEdit()"
            class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            {{ 'profile.modifier' | t }}
          </button>
        </div>
        <dl class="mt-4 grid gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.prenom' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ u.firstName || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.nom' | t }}</dt>
            <dd class="text-right text-gray-800 dark:text-white/90">{{ u.lastName || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500 dark:text-gray-400">Identifiant</dt>
            <dd class="text-right font-mono text-xs text-gray-700 dark:text-gray-300">{{ u.username || '—' }}</dd>
          </div>
          @if (u.address) {
            <div class="flex justify-between gap-4">
              <dt class="text-gray-500 dark:text-gray-400">{{ 'profile.adresse' | t }}</dt>
              <dd class="text-right text-gray-800 dark:text-white/90">{{ u.address }}</dd>
            </div>
          }
        </dl>
      </section>

      <!-- Account credentials (email + phone + password) -->
      <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
        <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'profile.identifiantsSecurite' | t }}</h3>

        <div class="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
          <!-- Email -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Email</p>
              <p class="mt-0.5 truncate text-sm text-gray-800 dark:text-white/90">{{ u.email || '—' }}</p>
            </div>
            <button
              type="button"
              (click)="openEmail()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>

          <!-- Phone -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'profile.telephone' | t }}</p>
              <p class="mt-0.5 truncate font-mono text-sm text-gray-800 dark:text-white/90">{{ u.phone || '—' }}</p>
            </div>
            <button
              type="button"
              (click)="openPhone()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>

          <!-- Password -->
          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ 'profile.motDePasse' | t }}</p>
              <p class="mt-0.5 text-sm text-gray-800 dark:text-white/90">••••••••••</p>
              @if (u.changePassword) {
                <p class="mt-0.5 text-xs text-warning-600 dark:text-warning-400">{{ 'profile.changementRequisALa' | t }}</p>
              }
            </div>
            <button
              type="button"
              (click)="openPassword()"
              class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Changer
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Roles & permissions (read-only) -->
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'profile.rolesPermissions' | t }}</h3>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.gereParUnAdministrateur' | t }}</p>

      <div class="mt-4 space-y-4">
        <div>
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ 'profile.rolesCount' | t: { count: businessRoles().length } }}
          </p>
          @if (businessRoles().length === 0) {
            <p class="text-sm text-gray-400">{{ 'profile.aucunRoleMetierAssigne' | t }}</p>
          } @else {
            <div class="flex flex-wrap gap-1.5">
              @for (r of businessRoles(); track r) {
                <span class="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ r }}</span>
              }
            </div>
          }
        </div>
        <div>
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Permissions fines ({{ permissions().length }})
          </p>
          @if (permissions().length === 0) {
            <p class="text-sm text-gray-400">{{ 'profile.aucunePermissionSpecifiqueAu' | t }}</p>
          } @else {
            <div class="flex flex-wrap gap-1">
              @for (p of permissions(); track p) {
                <span class="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ p }}</span>
              }
            </div>
          }
        </div>
      </div>
    </section>
  } @else {
    <section class="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
      <p class="text-sm text-gray-500 dark:text-gray-400">{{ 'profile.chargementDuProfil' | t }}</p>
    </section>
  }
</div>

<!-- ─────────────────────────────────────────────────────────────────────
     Personal info modal
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="editOpen()" (close)="closeEdit()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.modifierMesInformations' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ 'profile.prenomEtNomPour' | t }}</p>

  @if (editError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ editError() }}
    </div>
  }

  <form [formGroup]="editForm" (ngSubmit)="submitEdit()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.prenom' | t }}</label>
      <input
        type="text"
        formControlName="firstName"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.nom' | t }}</label>
      <input
        type="text"
        formControlName="lastName"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEdit()" [disabled]="editSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.annuler' | t }}</button>
      <button type="submit" [disabled]="editSaving() || editForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (editSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Password modal
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="passwordOpen()" (close)="closePassword()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.changerMonMotDe' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ 'profile.choisisUnMotDe' | t }}</p>

  @if (passwordError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ passwordError() }}
    </div>
  }
  @if (passwordOk()) {
    <div class="mt-4 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-700 dark:border-brand-800 dark:bg-brand-500/10 dark:text-brand-400">
      {{ 'profile.motDePasseModifie' | t }}
    </div>
  }

  <form [formGroup]="passwordForm" (ngSubmit)="submitPassword()" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.motDePasseActuel' | t }}</label>
      <input
        type="password"
        formControlName="oldPassword"
        autocomplete="current-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.nouveauMotDePasse' | t }}</label>
      <input
        type="password"
        formControlName="newPassword"
        autocomplete="new-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
      @if (passwordForm.controls.newPassword.touched && passwordForm.controls.newPassword.hasError('minlength')) {
        <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ 'profile.minimum8Caracteres' | t }}</p>
      }
    </div>
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.confirmerLeMotDe' | t }}</label>
      <input
        type="password"
        formControlName="newPasswordRepeat"
        autocomplete="new-password"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
      />
      @if (passwordForm.controls.newPasswordRepeat.touched && passwordForm.controls.newPasswordRepeat.hasError('passwordMismatch')) {
        <p class="mt-1 text-xs text-error-600 dark:text-error-400">{{ 'profile.lesMotsDePasse' | t }}</p>
      }
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closePassword()" [disabled]="passwordSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.fermer' | t }}</button>
      <button type="submit" [disabled]="passwordSaving() || passwordForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (passwordSaving()) { Modification… } @else { Modifier }
      </button>
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Email modal (2-step OTP)
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="emailOpen()" (close)="closeEmail()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.changerMonEmail' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (emailStep() === 'request') {
      {{ 'profile.renseigneTaNouvelleAdresse' | t }}
    } @else {
      Entre le code à 6 chiffres reçu sur <strong>{{ emailForm.controls.email.value }}</strong>.
    }
  </p>

  @if (emailError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ emailError() }}
    </div>
  }

  <form [formGroup]="emailForm" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Nouvel email</label>
      <input
        type="email"
        formControlName="email"
        [readonly]="emailStep() === 'verify'"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 disabled:opacity-60 read-only:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:read-only:bg-gray-800/60"
      />
    </div>

    @if (emailStep() === 'verify') {
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Code OTP (6 chiffres)</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          formControlName="otp"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-center font-mono text-lg tracking-widest text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
      </div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeEmail()" [disabled]="emailSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.annuler' | t }}</button>
      @if (emailStep() === 'request') {
        <button type="button" (click)="submitEmailRequest()" [disabled]="emailSaving() || emailForm.controls.email.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (emailSaving()) { Envoi… } @else { {{ 'profile.envoyerLeCode' | t }} }
        </button>
      } @else {
        <button type="button" (click)="submitEmailVerify()" [disabled]="emailSaving() || emailForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (emailSaving()) { {{ 'profile.verification' | t }} } @else { Confirmer }
        </button>
      }
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Phone modal (2-step OTP)
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="phoneOpen()" (close)="closePhone()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.changerMonTelephone' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (phoneStep() === 'request') {
      {{ 'profile.renseigneTonNouveauNumero' | t }}
    } @else {
      Entre le code à 6 chiffres reçu au <strong>{{ phoneForm.controls.phone.value }}</strong>.
    }
  </p>

  @if (phoneError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ phoneError() }}
    </div>
  }

  <form [formGroup]="phoneForm" class="mt-5 space-y-4">
    <div>
      <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.nouveauTelephone' | t }}</label>
      <input
        type="tel"
        formControlName="phone"
        [readonly]="phoneStep() === 'verify'"
        placeholder="+221 77 123 45 67"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 read-only:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:read-only:bg-gray-800/60"
      />
    </div>

    @if (phoneStep() === 'verify') {
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Code OTP (6 chiffres)</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          formControlName="otp"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-center font-mono text-lg tracking-widest text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        />
      </div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closePhone()" [disabled]="phoneSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.annuler' | t }}</button>
      @if (phoneStep() === 'request') {
        <button type="button" (click)="submitPhoneRequest()" [disabled]="phoneSaving() || phoneForm.controls.phone.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (phoneSaving()) { Envoi… } @else { {{ 'profile.envoyerLeCode' | t }} }
        </button>
      } @else {
        <button type="button" (click)="submitPhoneVerify()" [disabled]="phoneSaving() || phoneForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
          @if (phoneSaving()) { {{ 'profile.verification' | t }} } @else { Confirmer }
        </button>
      }
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Owner type switch modal (INDIVIDUAL ↔ COMPANY)
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="typeOpen()" (close)="closeType()" className="max-w-md p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.typeDeCompteProprietaire' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    {{ 'profile.particulierTuLouesTes' | t }}
  </p>

  @if (typeError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ typeError() }}
    </div>
  }

  <form [formGroup]="typeForm" (ngSubmit)="submitType()" class="mt-5 space-y-3">
    <label class="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition"
      [class.border-brand-500]="typeForm.controls.ownerType.value === 'INDIVIDUAL'"
      [class.bg-brand-50]="typeForm.controls.ownerType.value === 'INDIVIDUAL'"
      [class.dark:bg-brand-500/10]="typeForm.controls.ownerType.value === 'INDIVIDUAL'"
      [class.border-gray-200]="typeForm.controls.ownerType.value !== 'INDIVIDUAL'"
      [class.dark:border-gray-700]="typeForm.controls.ownerType.value !== 'INDIVIDUAL'"
    >
      <input type="radio" formControlName="ownerType" value="INDIVIDUAL" class="mt-0.5 h-4 w-4 accent-brand-500" />
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-gray-800 dark:text-white/90">Particulier</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.aucuneInformationProfessionnelleRequise' | t }}</p>
      </div>
    </label>

    <label class="flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition"
      [class.border-brand-500]="typeForm.controls.ownerType.value === 'COMPANY'"
      [class.bg-brand-50]="typeForm.controls.ownerType.value === 'COMPANY'"
      [class.dark:bg-brand-500/10]="typeForm.controls.ownerType.value === 'COMPANY'"
      [class.border-gray-200]="typeForm.controls.ownerType.value !== 'COMPANY'"
      [class.dark:border-gray-700]="typeForm.controls.ownerType.value !== 'COMPANY'"
    >
      <input type="radio" formControlName="ownerType" value="COMPANY" class="mt-0.5 h-4 w-4 accent-brand-500" />
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ 'profile.societeEntreprise' | t }}</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.tuPourrasSaisirUne' | t }}</p>
      </div>
    </label>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeType()" [disabled]="typeSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.annuler' | t }}</button>
      <button type="submit" [disabled]="typeSaving()" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (typeSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>

<!-- ─────────────────────────────────────────────────────────────────────
     Business info modal — édition des champs entreprise + flags ops
     ──────────────────────────────────────────────────────────────────── -->
<app-modal [isOpen]="bizOpen()" (close)="closeBiz()" className="max-w-lg p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ 'profile.informationsProprietaire' | t }}</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    @if (isCompany()) {
      {{ 'profile.informationsProfessionnellesContactEt' | t }}
    } @else {
      {{ 'profile.preferencesOperationnellesLesChamps' | t }}
    }
  </p>

  @if (bizError()) {
    <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
      {{ bizError() }}
    </div>
  }

  <form [formGroup]="bizForm" (ngSubmit)="submitBiz()" class="mt-5 space-y-4">
    @if (isCompany()) {
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Raison sociale</label>
          <input type="text" formControlName="businessName" [placeholder]="'profile.exSenegalAutoServices' | t"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Email pro</label>
          <input type="email" formControlName="businessEmail" placeholder="contact@..."
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
          @if (bizForm.controls.businessEmail.errors?.['email'] && bizForm.controls.businessEmail.touched) {
            <p class="mt-1 text-xs text-error-600">Email invalide.</p>
          }
        </div>
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.telephonePro' | t }}</label>
          <input type="text" formControlName="businessPhone" placeholder="+221 ..."
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
        <div class="sm:col-span-2">
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.adressePro' | t }}</label>
          <input type="text" formControlName="businessAddress" placeholder="Ex. : Plateau, Dakar"
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        </div>
      </div>

      <div class="border-t border-gray-200 pt-4 dark:border-gray-700"></div>
    }

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.tempsDeReponseMoyen' | t }}</label>
        <input type="text" formControlName="responseTime" [placeholder]="'profile.exMoinsDe2h' | t"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.afficheAuxLocatairesSur' | t }}</p>
      </div>
      <div class="flex items-end">
        <label class="flex w-full cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3 transition dark:border-gray-700">
          <input type="checkbox" formControlName="instantBooking" class="mt-0.5 h-4 w-4 accent-brand-500" />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-gray-800 dark:text-white/90">{{ 'profile.reservationImmediate' | t }}</p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.lesLocatairesPeuventReserver' | t }}</p>
          </div>
        </label>
      </div>
      <div>
        <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'profile.cautionParDefaut' | t }}</label>
        <input type="number" formControlName="defaultDepositRatePct" min="0" max="100" step="1" [placeholder]="'profile.aucune' | t"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90" />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ 'profile.appliqueATesVehicules' | t }}</p>
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeBiz()" [disabled]="bizSaving()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">{{ 'profile.annuler' | t }}</button>
      <button type="submit" [disabled]="bizSaving() || bizForm.invalid" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
        @if (bizSaving()) { Enregistrement… } @else { Enregistrer }
      </button>
    </div>
  </form>
</app-modal>
